import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { heroSlides } from '@/mocks/heroSlides';

const HEADER_H = 68;
/*
 * Polygon geometry, easing and image settle ported verbatim from
 * GSAP Vault "Image Clip Reveal" (direction: 'right' = opens from the left).
 * Copyright (c) 2026 GSAP Vault (gsapvault.com), MIT License.
 * https://github.com/jaydickinson/free-gsap-effects/tree/main/image-clip-reveal
 */
const FULL = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 100%, 0% 0%)';
const CLOSED = 'polygon(0% 0%, 0% 42%, 6% 50%, 0% 58%, 0% 100%, 0% 0%)';
const IMG_START_SCALE = 1.14;
const TEXT_OPEN = 'inset(-10% 0% -10% 0%)';
const TEXT_CLOSED = 'inset(-10% 100% -10% 0%)';
/** Input inactivity (ms) that marks the end of one wheel / trackpad gesture. */
const GESTURE_QUIET_MS = 300;
const LAST_STAGE = heroSlides.length - 1;
type GestureState = 'IDLE' | 'TRANSITIONING' | 'WAITING_FOR_GESTURE_END';
type VideoState = 'idle' | 'loading' | 'playing' | 'paused' | 'ended';

function useMedia(query: string) {
  const [match, setMatch] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const update = () => setMatch(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [query]);
  return match;
}

export default function HeroSection() {
  const isDesktop = useMedia('(min-width: 1024px)');
  const reduced = useMedia('(prefers-reduced-motion: reduce)');
  const sequence = isDesktop;
  const sectionRef = useRef<HTMLElement>(null);
  const [step, setStep] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [shown, setShown] = useState(0);
  const layerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const imgRefs = useRef<(HTMLImageElement | null)[]>([]);
  const titleRefs = useRef<(HTMLElement | null)[]>([]);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const stageElRef = useRef<HTMLDivElement>(null);
  // State only changes on media events (play / pause / end / error), never per frame
  const [vState, setVState] = useState<VideoState>('idle');
  const shownRef = useRef(0);
  // User explicitly paused: don't auto-resume until the stage changes
  const userPausedRef = useRef(false);
  const targetRef = useRef(0);
  const busyRef = useRef(true);
  const tlRef = useRef<gsap.core.Timeline | null>(null);
  const reducedRef = useRef(reduced);
  reducedRef.current = reduced;
  // Discrete gesture controller state
  const gestureRef = useRef<GestureState>('TRANSITIONING'); // locked until the intro finishes
  const stageRef = useRef(0);
  const lastWheelRef = useRef(-Infinity);
  const exitingRef = useRef(false);
  // One physical scroll gesture (a continuous wheel / trackpad stream, including its
  // inertia tail) may perform at most ONE command: advance one stage, or leave the hero.
  const gestureConsumedRef = useRef(false);
  const quietTimerRef = useRef(0);
  // Trackpad inertia decays monotonically; a sharp rise in |deltaY| means a new finger swipe
  const lastAbsRef = useRef(0);
  const commandAtRef = useRef(-Infinity);
  // Rising-edge validation: a real new swipe must follow a trough in the decaying stream
  const peakAbsRef = useRef(0);
  const troughAbsRef = useRef(Infinity);
  const settledAtRef = useRef(-Infinity);
  const exitDirRef = useRef(0);

  // When a transition finishes we return to the stable "locked on the current stage"
  // state. We never auto-advance here — only a brand-new gesture may move on.
  const settle = () => {
    if (gestureRef.current !== 'TRANSITIONING') return;
    window.clearTimeout(quietTimerRef.current);
    gestureRef.current = 'IDLE';
    settledAtRef.current = performance.now();
  };

  const metrics = () => {
    const el = sectionRef.current;
    if (!el) return { scrollable: 0, offset: -Infinity };
    return {
      scrollable: el.offsetHeight - (window.innerHeight - HEADER_H),
      offset: HEADER_H - el.getBoundingClientRect().top,
    };
  };

  // Park the page at the scroll position belonging to a stage (inside the sticky range)
  const scrollToStage = (n: number) => {
    const { scrollable, offset } = metrics();
    if (!Number.isFinite(offset)) return;
    window.scrollTo({
      top: window.scrollY + (scrollable * n) / LAST_STAGE - offset,
      behavior: 'instant' as 'auto',
    });
  };

  const goToStage = (n: number) => {
    const next = Math.min(Math.max(n, 0), LAST_STAGE);
    if (next === stageRef.current) return;
    stageRef.current = next;
    gestureRef.current = 'TRANSITIONING';
    setStep(next);
    scrollToStage(next);
  };

  const setLayer = (i: number, clip: string, z: number) => {
    const el = layerRefs.current[i];
    if (!el) return;
    el.style.clipPath = clip;
    el.style.zIndex = String(z);
  };

  const restore = (i: number) => {
    heroSlides.forEach((_, k) => setLayer(k, k === i ? FULL : CLOSED, k === i ? 2 : 1));
    imgRefs.current.forEach((im) => im && gsap.set(im, { scale: 1 }));
    titleRefs.current.forEach((t, k) => t && gsap.set(t, { clipPath: k === i ? TEXT_OPEN : TEXT_CLOSED, x: 0 }));
  };

  const openTl = (tl: gsap.core.Timeline, i: number, dur: number) => {
    const layer = layerRefs.current[i];
    tl.call(() => {
      setLayer(i, CLOSED, 3);
      gsap.set(imgRefs.current[i], { scale: IMG_START_SCALE });
      shownRef.current = i;
      setShown(i);
    });
    tl.fromTo(layer, { clipPath: CLOSED }, { clipPath: FULL, duration: dur, ease: 'expo.inOut' });
    tl.fromTo(
      imgRefs.current[i],
      { scale: IMG_START_SCALE },
      { scale: 1, duration: dur + 0.18, ease: 'power3.out' },
      '<',
    );
    // Headline wipes in from the left in sync with the image aperture
    tl.fromTo(
      titleRefs.current[i],
      { clipPath: TEXT_CLOSED, x: -24 },
      { clipPath: TEXT_OPEN, x: 0, duration: dur, ease: 'expo.inOut' },
      '<',
    );
    // Play cue + corner triangle fade back in part-way through every opening
    tl.call(() => setRevealed(true), [], `<+=${(dur * 0.62).toFixed(2)}`);
  };

  // Pause + rewind every video and return to the poster. Independent of GSAP.
  const stopVideos = () => {
    videoRefs.current.forEach((v) => {
      if (!v) return;
      v.pause();
      if (v.currentTime > 0) {
        try { v.currentTime = 0; } catch { /* metadata not loaded yet */ }
      }
    });
    userPausedRef.current = false;
    setVState('idle');
  };

  const playVideo = () => {
    const i = shownRef.current;
    const v = videoRefs.current[i];
    if (!v || !heroSlides[i].video) return;
    videoRefs.current.forEach((o, k) => { if (o && k !== i) o.pause(); });
    setVState('loading');
    try { v.currentTime = 0; } catch { /* ignore */ }
    v.muted = true;
    const p = v.play();
    if (p) p.catch(() => { if (shownRef.current === i) setVState('idle'); });
  };

  const togglePause = () => {
    const v = videoRefs.current[shownRef.current];
    if (!v || !heroSlides[shownRef.current].video) return;
    if (v.paused) {
      userPausedRef.current = false;
      if (v.currentTime === 0) { playVideo(); return; }
      const p = v.play();
      if (p) p.catch(() => setVState('idle'));
    } else {
      userPausedRef.current = true;
      v.pause();
    }
  };

  // Autoplay (muted) once the current stage has fully opened
  const autoPlay = () => {
    if (userPausedRef.current) return;
    const v = videoRefs.current[shownRef.current];
    if (!v || !v.paused) return;
    const r = stageElRef.current?.getBoundingClientRect();
    if (r && (r.bottom < r.height * 0.25 || r.top > window.innerHeight - r.height * 0.25)) return;
    playVideo();
  };

  const advance = () => {
    const from = shownRef.current;
    const goal = targetRef.current;
    if (from === goal) { busyRef.current = false; settle(); autoPlay(); return; }
    stopVideos();
    const to = from + (goal > from ? 1 : -1);
    if (reducedRef.current) {
      restore(to);
      shownRef.current = to;
      setShown(to);
      advance();
      return;
    }
    busyRef.current = true;
    const tl = gsap.timeline({
      onComplete: () => { restore(to); advance(); },
      onInterrupt: () => { restore(shownRef.current); setRevealed(true); },
    });
    tl.call(() => { setLayer(from, FULL, 2); setRevealed(false); });
    tl.to(layerRefs.current[from], { clipPath: CLOSED, duration: 0.38, ease: 'power3.inOut' });
    tl.to(titleRefs.current[from], { clipPath: TEXT_CLOSED, x: -24, duration: 0.38, ease: 'power3.inOut' }, '<');
    tl.call(() => setLayer(from, CLOSED, 1));
    openTl(tl, to, 1.1);
    tlRef.current = tl;
  };

  // Preload + entrance
  useEffect(() => {
    if (reduced) {
      restore(0);
      setRevealed(true);
      advance();
      return undefined;
    }
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      try {
        const tl = gsap.timeline({
          onComplete: () => { restore(shownRef.current); advance(); },
          onInterrupt: () => restore(shownRef.current),
        });
        openTl(tl, 0, 1.45);
        tlRef.current = tl;
      } catch {
        restore(0);
        setRevealed(true);
        advance();
      }
    };
    const first = imgRefs.current[0];
    if (first?.complete) start();
    else first?.addEventListener('load', start, { once: true });
    const fallback = window.setTimeout(start, 1500);
    return () => {
      window.clearTimeout(fallback);
      first?.removeEventListener('load', start);
      tlRef.current?.kill();
      restore(shownRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stop playback when the hero media leaves the viewport, the tab hides, or on unmount/route change
  useEffect(() => {
    const el = stageElRef.current;
    const videos = videoRefs.current;
    let io: IntersectionObserver | null = null;
    if (el && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(([entry]) => {
        if (entry && entry.intersectionRatio < 0.25) {
          const playing = videos.some((v) => v && !v.paused);
          if (playing) stopVideos();
        } else if (entry && !busyRef.current) {
          autoPlay();
        }
      }, { threshold: [0, 0.25] });
      io.observe(el);
    }
    const onHide = () => { if (document.hidden) videos.forEach((v) => v?.pause()); };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      io?.disconnect();
      document.removeEventListener('visibilitychange', onHide);
      videos.forEach((v) => v?.pause());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drive transitions from the single scroll controller's step
  useEffect(() => {
    targetRef.current = step;
    if (!busyRef.current) advance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  /*
   * ONE discrete controller (desktop only). Wheel input is a navigation
   * command: one gesture = at most one stage. It never drives animation
   * progress, speed or seek position.
   */
  useEffect(() => {
    if (!sequence) {
      stageRef.current = 0;
      setStep(0);
      return undefined;
    }

    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) return; // pinch-zoom
      if (e.deltaY === 0) return;

      const { scrollable, offset } = metrics();
      const inside = offset >= -2 && offset <= scrollable + 2;

      // Diagonal / mostly-horizontal swipes used to slip through as native vertical
      // scroll, which onScroll then snapped back → a one-frame jump (flicker).
      if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) {
        if (inside && !exitingRef.current) e.preventDefault();
        return;
      }

      const now = performance.now();
      const gap = now - lastWheelRef.current;
      lastWheelRef.current = now;
      const abs = Math.abs(e.deltaY);
      const prevAbs = lastAbsRef.current;
      lastAbsRef.current = abs;
      const dir = e.deltaY > 0 ? 1 : -1;

      // Track the decay of the current stream
      if (abs > peakAbsRef.current) { peakAbsRef.current = abs; troughAbsRef.current = abs; }
      if (abs < troughAbsRef.current) troughAbsRef.current = abs;

      // A quiet gap means the previous stream (and all its inertia) has fully died,
      // so this event begins a genuinely new gesture that is allowed one command.
      let fresh = false;
      if (gap > GESTURE_QUIET_MS) {
        fresh = true;
      } else if (
        // Rising edge inside an inertia tail: only accepted after a clear trough,
        // well after the last command, so inertia noise can never fake a swipe.
        now - commandAtRef.current > 350
        && troughAbsRef.current < peakAbsRef.current * 0.35
        && abs >= 15
        && abs > troughAbsRef.current * 2.5 + 4
        && abs > prevAbs * 1.6
      ) {
        fresh = true;
      }
      if (fresh) {
        gestureConsumedRef.current = false;
        exitingRef.current = false;
        peakAbsRef.current = abs;
        troughAbsRef.current = abs;
      }

      // Reversing direction after an exit grant cancels it (prevents sliding back
      // through the pinned hero natively with the stage frozen)
      if (exitingRef.current && inside && dir !== exitDirRef.current) {
        exitingRef.current = false;
        gestureConsumedRef.current = true;
      }

      if (!inside) return; // outside the hero: native scroll

      // While a flip plays, swallow every event so nothing queues or drifts.
      if (gestureRef.current === 'TRANSITIONING') {
        e.preventDefault();
        // A fresh swipe during a flip queues exactly one more stage (never an exit)
        if (!gestureConsumedRef.current && abs >= 4) {
          gestureConsumedRef.current = true;
          commandAtRef.current = now;
          const next = stageRef.current + dir;
          if (next >= 0 && next <= LAST_STAGE) goToStage(next);
        }
        return;
      }

      // This gesture already spent its single command. Keep absorbing the rest of the
      // stream (active overscroll + inertia) so it can never skip a stage; unless we
      // already granted an exit, in which case native scroll carries the page away.
      if (gestureConsumedRef.current) {
        if (exitingRef.current) return;
        e.preventDefault();
        return;
      }

      // Ignore the faint decaying tail that occasionally slips past the quiet gap.
      if (Math.abs(e.deltaY) < 4) { e.preventDefault(); return; }

      const stage = stageRef.current;
      const atBottom = stage === LAST_STAGE && offset >= scrollable - 2;
      const atTop = stage === 0 && offset <= 2;
      const exitDown = dir > 0 && atBottom;
      const exitUp = dir < 0 && atTop && window.scrollY > 1;

      if (exitDown || exitUp) {
        // An exit needs a genuinely new swipe that starts after the reveal has settled.
        // Leftover inertia from the flip-triggering swipe can no longer carry the page
        // straight past the second image.
        if (now - settledAtRef.current < 250 || now - commandAtRef.current < 600) {
          e.preventDefault();
          gestureConsumedRef.current = true;
          return;
        }
        exitingRef.current = true;         // release: native scroll leaves the pinned hero
        exitDirRef.current = dir;
        gestureConsumedRef.current = true;
        commandAtRef.current = now;
        return;
      }
      if (dir < 0 && atTop) {               // already at page top: nothing above to reach
        e.preventDefault();
        gestureConsumedRef.current = true;
        return;
      }

      // Normal case: this gesture advances exactly one stage, then locks.
      e.preventDefault();
      gestureConsumedRef.current = true;
      commandAtRef.current = now;
      goToStage(stage + dir);
    };

    const onScroll = () => {
      if (exitingRef.current) return;
      const { scrollable, offset } = metrics();
      if (!Number.isFinite(offset) || scrollable <= 0) return;
      if (offset < -1 || offset > scrollable + 1) {
        // Jumped outside (Home/End, anchor): keep the displayed stage consistent
        const edge = offset < -1 ? 0 : LAST_STAGE;
        if (stageRef.current !== edge) { stageRef.current = edge; setStep(edge); }
        return;
      }
      const wheelRecent = performance.now() - lastWheelRef.current < GESTURE_QUIET_MS * 2;
      if (wheelRecent) {
        // Momentum overshoot while entering / inside: hold the current stage position
        const pos = (scrollable * stageRef.current) / LAST_STAGE;
        if (Math.abs(offset - pos) > 1) scrollToStage(stageRef.current);
        return;
      }
      // Keyboard / scrollbar: position picks the stage (transitions still play one by one)
      const p = offset / scrollable;
      const n = Math.min(LAST_STAGE, Math.round(p * LAST_STAGE));
      if (n !== stageRef.current) { stageRef.current = n; setStep(n); }
    };

    onScroll();
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.clearTimeout(quietTimerRef.current);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequence]);

  const goNext = () => {
    const el = sectionRef.current;
    if (!el) return;
    if (sequence && stageRef.current < LAST_STAGE) {
      if (gestureRef.current === 'IDLE') goToStage(stageRef.current + 1);
      return;
    }
    const top = el.getBoundingClientRect().top + window.scrollY - HEADER_H;
    const behavior = reduced ? ('auto' as const) : ('smooth' as const);
    window.scrollTo({ top: top + el.offsetHeight, behavior });
  };

  const textFade = reduced ? '' : 'transition-all duration-700 ease-out';
  const current = heroSlides[shown];

  return (
    <section
      ref={sectionRef}
      data-header-orange
      className="theme-light relative lg:h-[calc(160svh-68px)]"
    >
      <div className="pt-5 md:pt-7 lg:pt-[30px] pb-14 md:pb-16 lg:pb-[clamp(32px,8svh,80px)] lg:sticky lg:top-[68px] lg:h-[calc(100svh-68px)] lg:flex lg:flex-col">
        <div className="px-5 sm:px-8 lg:px-0 lg:flex-1 lg:flex lg:flex-col lg:min-h-0">
          <figure className="mx-auto w-full lg:w-[88vw] max-w-[1320px] lg:flex-1 lg:flex lg:flex-col lg:min-h-0">
            <div
              className="relative w-full lg:flex-1 lg:min-h-[320px] lg:flex lg:flex-col"
              style={{ ['--cut' as string]: 'clamp(72px, 9vw, 150px)', ['--gap' as string]: '14px' }}
            >
            <div
              ref={stageElRef}
              data-motion="clip-reveal"
              className="hero-stage w-full aspect-[4/3] sm:aspect-[16/9] lg:aspect-auto lg:flex-1 relative overflow-hidden bg-transparent"
              style={{
                clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - var(--cut)), calc(100% - var(--cut)) 100%, 0 100%)',
              }}
            >
              {heroSlides.map((s, i) => (
                <div
                  key={s.id}
                  ref={(el) => { layerRefs.current[i] = el; }}
                  className="absolute inset-0 overflow-hidden will-change-[clip-path]"
                  style={{ clipPath: i === 0 && reduced ? FULL : CLOSED, zIndex: 1 }}
                >
                  <img
                    ref={(el) => { imgRefs.current[i] = el; }}
                    src={s.image}
                    alt={s.alt}
                    title={`Morrow People ${s.label.toLowerCase()} advisory`}
                    aria-hidden={i !== shown}
                    style={{ objectPosition: s.position }}
                    className="absolute inset-0 w-full h-full object-cover will-change-transform"
                  />
                  {s.video && (
                    <video
                      ref={(el) => { videoRefs.current[i] = el; }}
                      src={s.video}
                      muted
                      playsInline
                      preload="metadata"
                      controls={false}
                      disablePictureInPicture
                      aria-label={`${s.label} video`}
                      style={{ objectPosition: s.position }}
                      onPlaying={() => { if (shownRef.current === i) setVState('playing'); }}
                      onPause={(e) => {
                        const v = e.currentTarget;
                        if (shownRef.current === i && !v.ended && v.currentTime > 0) setVState('paused');
                      }}
                      onEnded={() => {
                        if (shownRef.current !== i) return;
                        userPausedRef.current = true; // don't auto-restart until the stage changes
                        setVState('ended');
                      }}
                      onError={() => { if (shownRef.current === i) setVState('idle'); }}
                      className={`absolute inset-0 w-full h-full object-cover bg-transparent transition-opacity ${vState === 'ended' ? 'duration-700' : 'duration-300'} ${
                        i === shown && (vState === 'playing' || vState === 'paused') ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/15 pointer-events-none" />
                </div>
              ))}

              <div className="absolute z-[5] top-4 left-4 md:top-6 md:left-6 flex items-center gap-3 text-[11px] tracking-[0.18em] uppercase text-white/90">
                <span className={textFade}>{`Morrow People — ${current.label}`}</span>
              </div>

              <div className="absolute z-[5] bottom-4 left-4 md:bottom-6 md:left-6 text-[13px] md:text-sm text-white/90 font-display italic">
                {heroSlides.map((s, i) => (
                  <span key={s.id} className={`${i === 0 ? 'relative' : 'absolute left-0 top-0'} whitespace-nowrap ${textFade} ${i === shown ? 'opacity-100' : 'opacity-0'}`}>
                    {s.phrase}
                  </span>
                ))}
              </div>

              {/* Whole media area: click to pause / resume */}
              {!!current.video && (
                <button
                  type="button"
                  onClick={togglePause}
                  aria-label={vState === 'playing' || vState === 'loading' ? `Pause ${current.label} video` : `Play ${current.label} video`}
                  className="absolute inset-0 z-[4] w-full h-full bg-transparent cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white"
                />
              )}

              {/* Paused cue: orange pill */}
              {(() => {
                const cueOn = (revealed || reduced) && vState === 'paused' && !!current.video;
                return (
                  <span
                    aria-hidden
                    className={`absolute z-[5] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none whitespace-nowrap hero-play-cue rounded-[6px] px-4 py-[9px] font-sans text-[14px] leading-none font-normal transition-opacity duration-300 ${cueOn ? 'opacity-100' : 'opacity-0'}`}
                  >
                    Click to play
                  </span>
                );
              })()}

              {sequence && (
                <div className="absolute z-[5] top-6 right-6 flex items-center gap-3 text-[11px] tracking-[0.18em] text-white/90">
                  <span>{`0${shown + 1} / 0${heroSlides.length}`}</span>
                  <div className="flex gap-1.5">
                    {heroSlides.map((s, i) => (
                      <span
                        key={s.id}
                        className={`h-px w-6 transition-colors duration-500 ${i <= shown ? 'bg-accent-500' : 'bg-white/35'}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={goNext}
              aria-label={sequence && step < LAST_STAGE ? 'Next image' : 'Scroll to next section'}
              className={`hero-next absolute right-0 bottom-0 group bg-accent-500 hover:bg-accent-600 cursor-pointer whitespace-nowrap transition-[background-color,opacity] duration-500 ${revealed || reduced ? 'opacity-100' : 'opacity-0'}`}
              style={{
                width: 'calc(var(--cut) - var(--gap) * 1.414)',
                height: 'calc(var(--cut) - var(--gap) * 1.414)',
                clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                transitionDelay: revealed ? '0.25s, 0.25s' : undefined,
              }}
            >
            </button>
            </div>

            <figcaption className="relative mt-5 md:mt-6 grid grid-cols-1 lg:grid-cols-[70%_30%] gap-3 md:gap-4 lg:gap-0 items-start">
              <div className="relative">
                {heroSlides.map((s, i) => {
                  const active = i === shown;
                  const Tag = i === 0 ? 'h1' : 'h2';
                  return (
                    <Tag
                      key={s.id}
                      ref={(el: HTMLHeadingElement | null) => { titleRefs.current[i] = el; }}
                      aria-hidden={!active}
                      style={{ clipPath: i === 0 && reduced ? TEXT_OPEN : TEXT_CLOSED }}
                      className={`font-display font-medium text-[clamp(1.75rem,2.9vw,2.6rem)] leading-[1.1] tracking-[-0.015em] text-foreground-950 lg:pr-16 max-w-[900px] will-change-[clip-path] ${active ? '' : 'pointer-events-none'} ${i === 0 ? 'relative' : 'absolute inset-x-0 top-0'}`}
                    >
                      {s.title}
                    </Tag>
                  );
                })}
              </div>
              <div className="relative">
                {heroSlides.map((s, i) => {
                  const active = i === shown;
                  return (
                    <p
                      key={s.id}
                      aria-hidden={!active}
                      className={`text-[15px] md:text-[16px] leading-relaxed text-foreground-700 lg:pt-2 max-w-[460px] ${textFade} ${active ? 'opacity-100' : 'opacity-0 pointer-events-none'} ${i === 0 ? 'relative' : 'absolute inset-x-0 top-0'}`}
                    >
                      {s.text}
                    </p>
                  );
                })}
              </div>
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}